import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBillingStore } from '@/stores/billingStore';
import Icon from '../components/Icon';

export const PlansPage = () => {
  const navigate = useNavigate();
  const { availablePlans, activeSubscription, isLoading, error, fetchPlans, fetchBillingStatus, addToCart } = useBillingStore();

  useEffect(() => {
    fetchPlans();
    fetchBillingStatus();
  }, [fetchPlans, fetchBillingStatus]);

  // OpenBanking plans only (id >= 5)
  const openBankingPlans = availablePlans.filter(p => p.id >= 5);

  // Fallback static plans if API hasn't returned data yet
  const plans = openBankingPlans.length > 0 ? openBankingPlans : [
    { id: 5, name: 'OpenBanking Monthly', numberOfMonths: 1, pricePerMonth: 9.90, features: [
      { name: '5 bank accounts', description: '', quantityType: 'Quantity', price: 0 },
      { name: 'Daily sync', description: '', quantityType: 'Unlimited', price: 0 },
      { name: '90-day history', description: '', quantityType: 'Quantity', price: 0 },
      { name: '50 credits/month', description: '', quantityType: 'Quantity', price: 0 },
    ]},
    { id: 6, name: 'OpenBanking Annual', numberOfMonths: 12, pricePerMonth: 7.90, discountPercent: 20, features: [
      { name: '10 bank accounts', description: '', quantityType: 'Quantity', price: 0 },
      { name: 'Daily sync', description: '', quantityType: 'Unlimited', price: 0 },
      { name: '365-day history', description: '', quantityType: 'Quantity', price: 0 },
      { name: '200 credits/month', description: '', quantityType: 'Quantity', price: 0 },
    ]},
  ];

  const handleSelectPlan = async (planId: number) => {
    const success = await addToCart(planId, 3); // productId 3 = OpenBanking Subscription
    if (success) {
      navigate('/billing/checkout');
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2">Choose Your Plan</h1>
      <p className="text-slate-500 dark:text-slate-400 mb-8">Select the plan that best fits your needs</p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = activeSubscription?.planId === plan.id;
          const isAnnual = plan.numberOfMonths === 12;

          return (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-slate-800 rounded-xl border-2 p-6 ${
                isAnnual
                  ? 'border-primary shadow-lg'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              {isAnnual && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-semibold rounded-full">
                  Best Value
                </div>
              )}

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{plan.name}</h3>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-800 dark:text-slate-200">
                  {plan.pricePerMonth.toFixed(2)}
                </span>
                <span className="text-slate-500 dark:text-slate-400">/month</span>
              </div>

              {isAnnual && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400 font-medium">
                  Save {plan.discountPercent}% — billed {(plan.pricePerMonth * 12).toFixed(2)}/year
                </p>
              )}

              <ul className="mt-6 space-y-3">
                {(plan.features || []).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Icon name="check_circle" className="text-green-500" />
                    {feature.name}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={isCurrentPlan}
                className={`mt-6 w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  isCurrentPlan
                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                    : isAnnual
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
